import React from 'react';
import {Trans, useTranslation} from 'next-i18next';
import {Button, Text} from '@tempo/core';
import type {
	FormFieldChange,
	ManagerArtist,
	ManagerFormData,
} from '../../types';
import {useClaims} from '@deezer/next/contexts/main/hooks';
import {ArrowRightIcon, PlusIcon} from '@tempo/icons';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import ArtistTable from '@deezer/next/features/ClaimAccess/components/ArtistTable';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';
import AutoIconButton from '@components/AutoIconButton';

type ArtistListManagerFormCardProps = {
	formData: ManagerFormData;
	onFormFieldChange: FormFieldChange<ManagerFormData>;
	onAddArtist: () => void;
	onEditArtist: (artist: ManagerArtist) => void;
	onBack: () => void;
	onSubmit: () => void;
	isSubmitting: boolean;
};

const ManagerArtistListFormCard = ({
	formData,
	onFormFieldChange,
	onAddArtist,
	onEditArtist,
	onBack,
	onSubmit,
	isSubmitting,
}: ArtistListManagerFormCardProps) => {
	const {t} = useTranslation('d4c');
	const claims = useClaims();
	const handleArtistEdit = (artist: ManagerArtist) => {
		onEditArtist(artist);
	};

	const handleArtistRemove = (artist: ManagerArtist) => {
		onFormFieldChange(
			'managerArtists',
			formData.managerArtists.filter(({id}) => id !== artist.id),
		);
	};

	const handleSubmit = () => {
		if (isSubmitting) return;
		onSubmit();
	};

	return (
		<PageLayout
			data-testid="manager-artist-list-card"
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							{claims.length > 0 && (
								<Button
									data-testid="back-button"
									variant="outline"
									onClick={onBack}
									size="small"
									leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // todo fix that
								>
									{t('generic_action_back_d4c-web')}
								</Button>
							)}
							<Button
								data-testid="send-request-button"
								onClick={handleSubmit}
								isDisabled={formData.managerArtists.length === 0}
								size="small"
								isLoading={isSubmitting}
							>
								{t('generic_action_sendrequest_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<FormSectionTitle
						title={t('requestaccessmanager_title_manageyourartists_d4c-web')}
						rightElements={
							<AutoIconButton
								data-testid="add-button"
								aria-label="add new artist"
								onClick={onAddArtist}
								size="small"
								leftIcon={<PlusIcon />}
							>
								{t('requestaccessmanager_title_addnewartist_d4c-web')}
							</AutoIconButton>
						}
					/>
					<Text>
						<Trans
							components={{
								strong: <strong />,
							}}
						>
							{t(
								'requestaccessmanager_text_toclaimartistprovidelivereleaseupc_d4c-web',
							)}
						</Trans>
					</Text>
					<FormSectionContainer>
						<ArtistTable
							artists={formData.managerArtists}
							onArtistEdit={handleArtistEdit}
							onArtistRemove={handleArtistRemove}
						/>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default ManagerArtistListFormCard;
